import {secureFileApi} from "@/container";

/**
 * A page of a scanned prescription, as the HDS serves it on its large
 * thumbnail.
 */
export interface SecurePrescriptionPage {
    blob: Blob;
    /**
     * How many pages the prescription has — sent with the first page only,
     * 1 on the others.
     */
    pageCount: number;
}

/**
 * The response header carrying the page count, lowercased by axios.
 */
const PAGE_COUNT_HEADER = 'x-page-count';

/**
 * How the HDS serves a prescription page by page: the large thumbnail of one
 * page at a time, the first one telling how many the prescription has.
 * Against an HDS that does not send the count, a prescription reads as one
 * page.
 *
 * Every image costs two requests against the per IP limit the HDS load
 * balancer applies to the whole pharmacy, so callers fetch a page only when
 * it is shown, and once.
 */
const SecurePrescriptionPageService = () => {
    return {
        /**
         * The large thumbnail url of a page; the first page keeps the url as
         * is.
         */
        pageUrl(url: string, page: number): string {
            return page === 1 ? url : `${url}${url.includes('?') ? '&' : '?'}page=${page}`;
        },

        /**
         * Fetch a page of the prescription behind a large thumbnail url.
         */
        fetch(url: string, page: number): Promise<SecurePrescriptionPage> {
            return secureFileApi().getWithHeaders(this.pageUrl(url, page))
                .then(({blob, headers}) => ({
                    blob,
                    pageCount: page === 1 ? Math.max(1, Number(headers[PAGE_COUNT_HEADER]) || 1) : 1
                }));
        }
    };
};

export default SecurePrescriptionPageService();
